import { ThreadID, DEEP_DIVE_INDEX } from '@/models/thread';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

// The hint shows first so the eye is on the switch when it flips. The switch
// delay counts from when the hint is on the page, not from the start, so a
// slow render can't make both steps land at once.
const HINT_DELAY = 300;
const SWITCH_DELAY = 400;

// Set by "Don't show this again"; until then the hint shows every time
const HINT_DISMISSED_KEY = 'deep-dive-auto-hint-dismissed';

const canShowHint = () => {
  try {
    return localStorage.getItem(HINT_DISMISSED_KEY) !== 'true';
  } catch {
    return true;
  }
};

const dismissHintForGood = () => {
  try {
    localStorage.setItem(HINT_DISMISSED_KEY, 'true');
  } catch {
    // Storage can be blocked; the hint then keeps showing
  }
};

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export interface DeepDiveAutoSelect {
  // Sending and toggling wait while this is true
  isPending: boolean;
  isHintVisible: boolean;
  hideHint: () => void;
  dismissHint: () => void;
  // Drops a pending switch and the hint without turning Deep Dive on
  cancel: () => void;
}

/*
 * Turns Deep Dive on when the user opens a thread whose last request used it:
 * the hint appears, then the switch flips. Leaving the thread cancels both.
 */
const useDeepDiveAutoSelect = ({
  threadID,
  setSelectedApproach,
  pushUserEvent,
}: {
  threadID: ThreadID;
  setSelectedApproach: Dispatch<SetStateAction<number>>;
  pushUserEvent: (action: string) => void;
}): DeepDiveAutoSelect & { start: (id: string) => void } => {
  const [pending, setPending] = useState<{ id: string; withHint: boolean }>();
  const [hintID, setHintID] = useState<string>();

  // pushUserEvent changes while the session loads; reading it through a ref
  // keeps that from restarting the timers below
  const pushUserEventRef = useRef(pushUserEvent);
  useEffect(() => {
    pushUserEventRef.current = pushUserEvent;
  }, [pushUserEvent]);

  const selectDeepDive = useCallback(() => {
    setSelectedApproach(DEEP_DIVE_INDEX);
    pushUserEventRef.current('auto_approach_deep-dive');
  }, [setSelectedApproach]);

  const finish = useCallback(() => {
    setPending(undefined);
    selectDeepDive();
  }, [selectDeepDive]);

  const start = useCallback(
    (id: string) => {
      // Without motion there is nothing to watch, so skip the wait
      if (prefersReducedMotion()) {
        selectDeepDive();
        if (canShowHint()) setHintID(id);
        return;
      }
      setPending({ id, withHint: canShowHint() });
    },
    [selectDeepDive]
  );

  // Step 1: show the hint, or flip right away when it's not shown
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(
      () => (pending.withHint ? setHintID(pending.id) : finish()),
      HINT_DELAY
    );
    return () => clearTimeout(timer);
  }, [pending, finish]);

  // Step 2: once the hint is on the page, flip the switch
  useEffect(() => {
    if (!pending || hintID !== pending.id) return;
    const timer = setTimeout(finish, SWITCH_DELAY);
    return () => clearTimeout(timer);
  }, [pending, hintID, finish]);

  // Leaving the thread cancels a pending switch, so its timers can't turn
  // Deep Dive on in the next thread, and drops the hint, so it doesn't pop
  // back up on return. isHintVisible also checks the thread, which covers the
  // render before this effect runs.
  useEffect(() => {
    setPending((current) => (current?.id === threadID ? current : undefined));
    setHintID((id) => (id === threadID ? id : undefined));
  }, [threadID]);

  // Closing the hint before the switch flips (Escape, the cross, "Don't show
  // this again") skips the rest of the wait
  const hideHint = useCallback(() => {
    setHintID(undefined);
    if (pending) finish();
  }, [pending, finish]);

  const dismissHint = useCallback(() => {
    dismissHintForGood();
    hideHint();
    pushUserEventRef.current('dismiss_deep_dive_hint');
  }, [hideHint]);

  // For when something else sets the approach: the hint would then explain a
  // change it didn't make
  const cancel = useCallback(() => {
    setPending(undefined);
    setHintID(undefined);
  }, []);

  return {
    start,
    isPending: !!threadID && pending?.id === threadID,
    isHintVisible: !!threadID && hintID === threadID,
    hideHint,
    dismissHint,
    cancel,
  };
};

export default useDeepDiveAutoSelect;
